import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { getAssistQuotes, getDestinations } from '@shared/services/services';

import DateRangeGit from '@shared/components/CustomDatePicker';

import imageGIT from '../../../assets/ImageSearchForm.jpg';
import imageDesk from '../../../assets/assistSearch.svg';
import imageMobile from '../../../assets/assistSearchMobile.svg';

import useWindowSize from '@shared/hooks/useWindowsSize';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { saveSearchAssistDetails } from '@shared/redux/actions';

import { postMetaConverter } from '@shared/services/meta';

import useVersionControl from '@shared/hooks/useVersionControl';
import { ASSIST_SEARCH } from '@shared/helpers/routes';

import GenericDropdown from '@shared/components/GenericDropdown';

import PassengersDropdown from './PassengersDropdown';

import SearchPanel from '@shared/components/SearchPanel';

import { SearchButtonWrapper } from '@shared/components/SearchButtonWrapper';

import { sxStyle } from './styles';

let destinationsCache = null;

export const FormSearchAssist = ({
  isDetailPage = false,
  isHomePage = false,
  setReady,
  needsNavigate,
  setIsLoadingResults,
  setSearchResults,
  setXtransactioId,
}) => {
  const { checkVersion } = useVersionControl(ASSIST_SEARCH);

  const screenSize = useWindowSize();

  const searchInfo = useSelector(store => store.searchAssistReducer);
  const pageConfiguration = useSelector(store => store.pageConfiguration);

  const user = useSelector(store => store.userInfo);

  const dispatch = useDispatch();

  const isMounted = useRef(true);

  const [hasError, setHasError] = useState(false);
  const [destinations, setDestinations] = useState([]);

  const [destinationSelected, setDestinationSelected] = useState(
    searchInfo.destination || ''
  );

  const [showPassangersModal, setShowPassangersModal] = useState(false);

  const {
    l70: initialLessThan70 = 0,
    l75: initialLessThan75 = 0,
    m75: initialMoreThan75 = 0,
  } = searchInfo.passengersv2 || {};

  const [lessThan70, setPassengerLessThan70] = useState(initialLessThan70);
  const [lessThan75, setPassengerLessThan75] = useState(initialLessThan75);
  const [moreThan75, setPassengerMoreThan75] = useState(initialMoreThan75);

  const [calendarValue, setCalendarValue] = useState({
    check_in: searchInfo.checkIn,
    check_out: searchInfo.checkOut,
    total_nights: searchInfo.totalNights,
  });

  const getNewPassengerValue = (currentValue, action) => {
    let newVal = currentValue + (action == 1 ? 1 : -1);
    return newVal >= 0 ? newVal : 0;
  };

  const managePassengers = (action, cases) => {
    switch (cases) {
      case 1:
        setPassengerLessThan70(getNewPassengerValue(lessThan70, action));
        break;
      case 2:
        setPassengerLessThan75(getNewPassengerValue(lessThan75, action));
        break;
      case 3:
        setPassengerMoreThan75(getNewPassengerValue(moreThan75, action));
        break;
      default:
        return;
    }
  };

  function formatDate(dateString) {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  const bannerTdh = screenSize.width > 600 ? imageDesk : imageMobile;
  const image = pageConfiguration.id == 1 ? bannerTdh : imageGIT;

  const handleChange = event => {
    const selectedId = event.target.value;
    const selectedDestination = destinations.find(opt => opt.id === selectedId);
    setDestinationSelected(selectedDestination);
    if (selectedDestination)
      try {
        if (window.opc) window.opc();
      } catch (error) {
        console.error('assist handleChange', error);
      }
  };

  const updateCalendarValues = ({ check_in, check_out, total_nights }) => {
    setCalendarValue({ check_in, check_out, total_nights });
    const isValidForm =
      moment(check_in, 'YYYY-MM-DD', true).isValid() &&
      moment(check_out, 'YYYY-MM-DD', true).isValid();
    if (isValidForm) {
      setHasError(false);
    }
  };

  const getArrayPassengers = {
    under_70: lessThan70,
    under_75: lessThan75,
    over_75: moreThan75,
  };

  const fetchDestinations = async () => {
    if (destinationsCache) {
      setDestinations(destinationsCache);
      return;
    }

    try {
      const result = await getDestinations();
      destinationsCache = result;
      if (isMounted.current) {
        setDestinations(result);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchData = async () => {
    const hasPassengers = Object.values(getArrayPassengers).some(
      count => count > 0
    );

    if (
      !!searchInfo.checkIn &&
      !!searchInfo.checkOut &&
      !isDetailPage &&
      hasPassengers &&
      !isHomePage
    ) {
      checkVersion({ type: 'assist', infoSearch: searchInfo, force: true });

      try {
        if (isMounted.current) setIsLoadingResults(true);
        let result = await getAssistQuotes({
          start_date: formatDate(calendarValue.check_in),
          end_date: formatDate(calendarValue.check_out),
          destination: destinationSelected.code,
          passengers: getArrayPassengers,
          id_user: user?.user?.id || null,
          family_plan: false,
        });
        if (isMounted.current) {
          setSearchResults(result.data);
          setXtransactioId(result.id_transaction);
        }
      } catch (e) {
        console.error('Error', e);
        if (isMounted.current) {
          setSearchResults({
            hotels: [],
            tokens: [],
          });
        }
      } finally {
        if (isMounted.current) setIsLoadingResults(false);
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;

    fetchData();
    fetchDestinations();

    return () => {
      isMounted.current = false;
    };
  }, []);

  const formHandler = () => {
    if (showPassangersModal) {
      setShowPassangersModal(false);
    }
    const isValidForm =
      moment(calendarValue.check_in, 'YYYY-MM-DD', true).isValid() &&
      moment(calendarValue.check_out, 'YYYY-MM-DD', true).isValid();

    if (
      !isValidForm ||
      lessThan70 + lessThan75 + moreThan75 === 0 ||
      !destinationSelected
    ) {
      setHasError(true);
      setReady(false);
    } else {
      dispatch(
        saveSearchAssistDetails({
          checkIn: calendarValue.check_in,
          checkOut: calendarValue.check_out,
          totalNights: calendarValue.total_nights,
          destination: destinationSelected,
          passengers: getArrayPassengers,
          passengersv2: {
            l70: lessThan70,
            l75: lessThan75,
            m75: moreThan75,
          },
          id_user: null,
          family_plan: false,
        })
      );
      const value = {
        fecha_desde: calendarValue.check_in,
        fecha_hasta: calendarValue.check_out,
        destino: destinationSelected,
        pasajeros: getArrayPassengers,
      };
      window.dataLayer.push({
        event: 'busquedaAssist',
        eventProps: {
          category: 'asistencia',
          action: 'submit',
          label: 'buscar',
          value,
        },
      });

      postMetaConverter(
        {
          event_name: 'busquedaAssist',
          custom_data: {
            category: 'asistencia',
            action: 'submit',
            label: 'buscar',
            ...value,
          },
        },
        user
      );

      setHasError(false);
      if (needsNavigate) {
        setReady(true);
      } else {
        fetchData();
      }
    }
  };

  const validSelectedId =
    destinations?.length &&
    destinations.some(opt => opt.id === destinationSelected?.id)
      ? destinationSelected.id
      : '';

  return (
    <SearchPanel title="Assist Card" image={image}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Grid item xs={12} md={4} sx={sxStyle.cityInputButtonContainer}>
          <GenericDropdown
            id="destination-select"
            menuItemHeigh={50}
            formControlHeight={55}
            items={destinations.map(opt => ({
              id: opt.id,
              icon: null,
              fragments: [{ text: opt.description, bold: false }],
            }))}
            selectedItem={
              destinations.find(opt => opt.id === validSelectedId) && {
                id: validSelectedId,
                icon: <LocationOnIcon sx={{ color: '#0000008f' }} />,
                fragments: [
                  {
                    text: destinations.find(opt => opt.id === validSelectedId)
                      .description,
                    bold: false,
                  },
                ],
              }
            }
            onSelect={item => handleChange({ target: { value: item.id } })}
            buttonLabel="Seleccioná una opción"
            labelIcon={<LocationOnIcon sx={{ color: '#0000008f' }} />}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          sx={{
            margin: {
              xs: '0 auto',
              sm: 0,
            },
            marginTop: {
              xs: '11px',
              sm: 0,
            },
          }}
        >
          <DateRangeGit
            checkInParam={calendarValue?.check_in}
            checkOutParam={calendarValue?.check_out}
            placeholders={{ startDate: 'Entrada', endDate: 'Salida' }}
            dateInfo={value =>
              updateCalendarValues({
                check_in: value.check_in,
                check_out: value.check_out,
                total_nights: value.total_nights,
              })
            }
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          sx={{
            mt: { xs: '11px', sm: 0 },
            m: { xs: '0px auto', sm: 0 },
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            {showPassangersModal && (
              <Box
                component="button"
                type="button"
                onClick={() => setShowPassangersModal(false)}
                sx={sxStyle.paxLockScreen}
              />
            )}

            <Grid
              item
              xs={12}
              md={8}
              sx={{
                position: 'relative',
                pr: { xs: 0, md: '8px' },
              }}
            >
              <PassengersDropdown
                lessThan70={lessThan70}
                lessThan75={lessThan75}
                moreThan75={moreThan75}
                managePassengers={managePassengers}
              />
            </Grid>

            <SearchButtonWrapper
              id="assistSearchButton"
              onClick={() => formHandler()}
              label="BUSCAR"
            />
          </Grid>
        </Grid>

        <Grid sx={{ display: 'flex' }}>
          {hasError && !destinationSelected && (
            <Grid>
              <Typography variant="subtitle2" color="error">
                Complete todos los campos para realizar la búsqueda.
              </Typography>
            </Grid>
          )}
          {hasError && lessThan70 + lessThan75 + moreThan75 === 0 && (
            <Grid>
              <Typography
                variant="subtitle2"
                color="error"
                sx={{ marginLeft: '5px' }}
              >
                Indique el rango de edades de los pasajeros.
              </Typography>
            </Grid>
          )}
          {hasError &&
            !(lessThan70 + lessThan75 + moreThan75 === 0) &&
            !!destinationSelected && (
              <Grid>
                <Typography
                  variant="subtitle2"
                  color="error"
                  sx={{ marginLeft: '20px' }}
                >
                  Indique las fechas para la búsqueda
                </Typography>
              </Grid>
            )}
        </Grid>
      </Grid>
    </SearchPanel>
  );
};
